import { useEffect, useState } from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import type { SettingMeta } from '@/lib/settings-categories'
import {
  BusinessHoursEditor,
  type BusinessHoursValue,
} from '@/components/settings/BusinessHoursEditor'
import { PhoneListEditor } from '@/components/settings/PhoneListEditor'

export interface SettingEditorSubmit {
  value: unknown
  rationale: string
}

interface SettingEditorProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  meta: SettingMeta
  currentValue: unknown
  onSubmit: (input: SettingEditorSubmit) => void | Promise<void>
  submitting?: boolean
  serverError?: string | null
}

const RATIONALE_MIN = 10
const RATIONALE_MAX = 500

/**
 * `<SettingEditor />` — per-type editor for a single `system_settings` key.
 *
 * Story 3.16 §AC #7 + #8:
 *   - number / string / textarea / boolean / object / array / enum editors
 *   - inline JSON parse + per-key client-side validation (object/array)
 *   - diff banner "antes → depois" before submission
 *   - mandatory `rationale` (>=10 chars) appended to the audit row
 *
 * The component is intentionally schema-agnostic at the type level — it works
 * with whatever the parent passes via `meta.editor`. Backend validation is the
 * source of truth; this UI just gives faster feedback.
 */
export function SettingEditor({
  open,
  onOpenChange,
  meta,
  currentValue,
  onSubmit,
  submitting = false,
  serverError = null,
}: SettingEditorProps): React.ReactElement {
  // Internal draft state — we hold a stringified version of the value to let
  // the user freely type, then parse on submit (for object/array editors).
  const [draftString, setDraftString] = useState<string>('')
  const [draftBoolean, setDraftBoolean] = useState<boolean>(false)
  const [rationale, setRationale] = useState<string>('')
  const [clientError, setClientError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setClientError(null)
      setRationale('')
      switch (meta.editor) {
        case 'object':
        case 'array':
          setDraftString(stringifyJson(currentValue))
          break
        case 'boolean':
          setDraftBoolean(currentValue === true)
          break
        case 'number':
          setDraftString(
            typeof currentValue === 'number' && Number.isFinite(currentValue)
              ? String(currentValue)
              : '',
          )
          break
        default:
          setDraftString(typeof currentValue === 'string' ? currentValue : '')
      }
    }
  }, [open, meta.editor, currentValue])

  function parseDraft(): { ok: true; value: unknown } | { ok: false; error: string } {
    switch (meta.editor) {
      case 'number': {
        const n = Number(draftString)
        if (!Number.isFinite(n)) return { ok: false, error: 'Valor deve ser numérico.' }
        if (meta.min !== undefined && n < meta.min)
          return { ok: false, error: `Valor mínimo: ${meta.min}.` }
        if (meta.max !== undefined && n > meta.max)
          return { ok: false, error: `Valor máximo: ${meta.max}.` }
        return { ok: true, value: n }
      }
      case 'boolean':
        return { ok: true, value: draftBoolean }
      case 'enum':
        if (
          meta.options === undefined ||
          !meta.options.includes(draftString) ||
          draftString === ''
        ) {
          return { ok: false, error: 'Selecione uma opção válida.' }
        }
        return { ok: true, value: draftString }
      case 'object':
      case 'array': {
        if (draftString.trim() === '') return { ok: false, error: 'JSON não pode ser vazio.' }
        try {
          const parsed = JSON.parse(draftString) as unknown
          if (meta.editor === 'array' && !Array.isArray(parsed)) {
            return { ok: false, error: 'JSON deve ser um array.' }
          }
          if (
            meta.editor === 'object' &&
            (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed))
          ) {
            return { ok: false, error: 'JSON deve ser um objeto.' }
          }
          return { ok: true, value: parsed }
        } catch (err) {
          return { ok: false, error: `JSON inválido: ${(err as Error).message}` }
        }
      }
      case 'string':
      case 'textarea':
      default:
        return { ok: true, value: draftString }
    }
  }

  const parsed = parseDraft()
  const beforeText = stringifyJson(currentValue)
  const afterText = parsed.ok ? stringifyJson(parsed.value) : draftString
  const changed = beforeText !== afterText

  function handleSubmit(e: React.FormEvent<HTMLFormElement>): void {
    e.preventDefault()
    setClientError(null)
    if (!parsed.ok) {
      setClientError(parsed.error)
      return
    }
    if (rationale.trim().length < RATIONALE_MIN) {
      setClientError(`Justificativa (rationale) precisa de ao menos ${RATIONALE_MIN} caracteres.`)
      return
    }
    if (rationale.length > RATIONALE_MAX) {
      setClientError(`Justificativa não pode exceder ${RATIONALE_MAX} caracteres.`)
      return
    }
    void onSubmit({ value: parsed.value, rationale: rationale.trim() })
  }

  const errorMessage = clientError ?? serverError

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            'fixed inset-0 z-50 bg-black/60',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          )}
        />
        <DialogPrimitive.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-50 max-h-[90vh] w-full max-w-2xl -translate-x-1/2 -translate-y-1/2 overflow-y-auto',
            'rounded-lg border bg-background p-6 shadow-lg',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          )}
        >
          <div className="flex items-start justify-between">
            <div className="space-y-1.5">
              <DialogPrimitive.Title className="text-lg font-semibold">
                Editar: {meta.label}
              </DialogPrimitive.Title>
              <DialogPrimitive.Description className="text-sm text-muted-foreground">
                <code className="font-mono">{meta.key}</code>
                {meta.hint !== undefined && <> — {meta.hint}</>}
              </DialogPrimitive.Description>
            </div>
            <DialogPrimitive.Close
              className="rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring"
              aria-label="Fechar"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </DialogPrimitive.Close>
          </div>

          <form onSubmit={handleSubmit} className="mt-4 space-y-4">
            {meta.key === 'business_hours' ? (
              <BusinessHoursEditorAdapter
                draftString={draftString}
                initialValue={currentValue}
                onChange={(next) => setDraftString(JSON.stringify(next))}
                onValidityChange={(valid, errors) => {
                  setClientError(valid ? null : errors.join(' '))
                }}
                disabled={submitting}
              />
            ) : meta.key === 'ghl_test_phone_allowlist' ? (
              <PhoneListEditorAdapter
                draftString={draftString}
                onChange={(next) => setDraftString(JSON.stringify(next))}
                onValidityChange={(valid, errors) => {
                  setClientError(valid ? null : errors.join(' '))
                }}
                disabled={submitting}
              />
            ) : (
            <div className="space-y-2">
              <Label htmlFor="setting-editor-value">Novo valor</Label>
              {meta.editor === 'number' && (
                <Input
                  id="setting-editor-value"
                  type="number"
                  value={draftString}
                  min={meta.min}
                  max={meta.max}
                  step={meta.step}
                  onChange={(e) => setDraftString(e.target.value)}
                />
              )}
              {meta.editor === 'string' && (
                <Input
                  id="setting-editor-value"
                  value={draftString}
                  onChange={(e) => setDraftString(e.target.value)}
                />
              )}
              {meta.editor === 'textarea' && (
                <Textarea
                  id="setting-editor-value"
                  value={draftString}
                  rows={6}
                  onChange={(e) => setDraftString(e.target.value)}
                />
              )}
              {meta.editor === 'boolean' && (
                <label className="inline-flex items-center gap-2 text-sm">
                  <input
                    id="setting-editor-value"
                    type="checkbox"
                    checked={draftBoolean}
                    onChange={(e) => setDraftBoolean(e.target.checked)}
                    className="h-4 w-4"
                  />
                  {draftBoolean ? 'Ativado' : 'Desativado'}
                </label>
              )}
              {meta.editor === 'enum' && (
                <Select
                  id="setting-editor-value"
                  value={draftString}
                  onChange={(e) => setDraftString(e.target.value)}
                >
                  <option value="" disabled>
                    Selecione…
                  </option>
                  {(meta.options ?? []).map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </Select>
              )}
              {(meta.editor === 'object' || meta.editor === 'array') && (
                <Textarea
                  id="setting-editor-value"
                  value={draftString}
                  rows={8}
                  spellCheck={false}
                  className="font-mono text-xs"
                  onChange={(e) => setDraftString(e.target.value)}
                />
              )}
            </div>
            )}

            <div className="rounded-md border bg-muted/30 p-3">
              <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Diff
              </div>
              <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2">
                <div>
                  <div className="text-xs text-muted-foreground">Antes</div>
                  <pre
                    aria-label="Valor antes"
                    className="mt-1 whitespace-pre-wrap break-words rounded bg-background p-2 text-xs"
                  >
                    {beforeText}
                  </pre>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Depois</div>
                  <pre
                    aria-label="Valor depois"
                    className={cn(
                      'mt-1 whitespace-pre-wrap break-words rounded p-2 text-xs',
                      changed ? 'bg-primary/10' : 'bg-background',
                    )}
                  >
                    {afterText}
                  </pre>
                </div>
              </div>
              {!changed && (
                <p className="mt-2 text-xs text-muted-foreground">
                  Nenhuma mudança ainda — edite o valor acima.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="setting-editor-rationale">
                Justificativa (rationale) <span className="text-destructive">*</span>
              </Label>
              <Textarea
                id="setting-editor-rationale"
                value={rationale}
                rows={3}
                minLength={RATIONALE_MIN}
                maxLength={RATIONALE_MAX}
                placeholder="Por que essa mudança? (mín. 10 caracteres — vai pro audit log)"
                onChange={(e) => setRationale(e.target.value)}
                required
              />
              <p className="text-xs text-muted-foreground">
                {rationale.length} / {RATIONALE_MAX} caracteres
              </p>
            </div>

            {errorMessage !== null && (
              <p
                role="alert"
                className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive"
              >
                {errorMessage}
              </p>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <DialogPrimitive.Close asChild>
                <Button type="button" variant="outline" disabled={submitting}>
                  Cancelar
                </Button>
              </DialogPrimitive.Close>
              <Button type="submit" disabled={submitting || !changed}>
                {submitting ? 'Salvando…' : 'Salvar'}
              </Button>
            </div>
          </form>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

function stringifyJson(value: unknown): string {
  if (value === undefined || value === null) return 'null'
  if (typeof value === 'string') return value
  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return String(value)
  }
}

// ===========================================================================
// Story 3.16.1 — adapter para business_hours custom editor
// ===========================================================================

interface BusinessHoursEditorAdapterProps {
  draftString: string
  initialValue: unknown
  onChange: (next: BusinessHoursValue) => void
  onValidityChange: (valid: boolean, errors: string[]) => void
  disabled?: boolean
}

const DEFAULT_BUSINESS_HOURS: BusinessHoursValue = {
  timezone: 'America/Sao_Paulo',
  start: '08:00',
  end: '18:00',
  days: ['mon', 'tue', 'wed', 'thu', 'fri'],
}

/**
 * Story 3.16.1 — adapter que ponte SettingEditor (string-based draft state)
 * com BusinessHoursEditor (object-based value).
 *
 * - parseia `draftString` → `BusinessHoursValue`; fallback para
 *   `initialValue` válido OR default seed quando parse falha
 * - propaga `onChange` re-stringificando o objeto para draftString (mantém
 *   o diff antes/depois do SettingEditor funcionando inalterado)
 * - encaminha `initialValue` para o BusinessHoursEditor calcular delta
 *   "+Xh/semana" no preview
 */
function BusinessHoursEditorAdapter({
  draftString,
  initialValue,
  onChange,
  onValidityChange,
  disabled = false,
}: BusinessHoursEditorAdapterProps): React.ReactElement {
  const parsed = parseBusinessHoursOrDefault(draftString, initialValue)
  const initialParsed = parseBusinessHoursOrDefault(stringifyJson(initialValue), initialValue)
  return (
    <BusinessHoursEditor
      value={parsed}
      initialValue={initialParsed}
      onChange={onChange}
      onValidityChange={onValidityChange}
      disabled={disabled}
    />
  )
}

function parseBusinessHoursOrDefault(
  raw: string,
  fallback: unknown,
): BusinessHoursValue {
  // 1) tenta parsear o draftString (json stringificado pelo nosso fluxo)
  try {
    const parsed = JSON.parse(raw) as unknown
    if (isBusinessHoursShape(parsed)) return parsed
  } catch {
    // ignore — segue para fallback
  }
  // 2) fallback: usa o initialValue se for válido
  if (isBusinessHoursShape(fallback)) return fallback
  // 3) seed default para nunca renderizar com value inválido
  return { ...DEFAULT_BUSINESS_HOURS, days: [...DEFAULT_BUSINESS_HOURS.days] }
}

function isBusinessHoursShape(v: unknown): v is BusinessHoursValue {
  if (v === null || typeof v !== 'object') return false
  const obj = v as Record<string, unknown>
  if (typeof obj.timezone !== 'string') return false
  if (typeof obj.start !== 'string') return false
  if (typeof obj.end !== 'string') return false
  if (!Array.isArray(obj.days)) return false
  const valid = new Set(['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'])
  return obj.days.every((d) => typeof d === 'string' && valid.has(d))
}

// ===========================================================================
// PhoneListEditorAdapter — `ghl_test_phone_allowlist` custom editor
// ===========================================================================

interface PhoneListEditorAdapterProps {
  draftString: string
  onChange: (next: string[]) => void
  onValidityChange: (valid: boolean, errors: string[]) => void
  disabled?: boolean
}

function PhoneListEditorAdapter({
  draftString,
  onChange,
  onValidityChange,
  disabled = false,
}: PhoneListEditorAdapterProps): React.ReactElement {
  const phones = parsePhoneListOrDefault(draftString)
  return (
    <PhoneListEditor
      value={phones}
      onChange={onChange}
      onValidityChange={onValidityChange}
      disabled={disabled}
    />
  )
}

function parsePhoneListOrDefault(raw: string): string[] {
  try {
    const parsed = JSON.parse(raw) as unknown
    if (Array.isArray(parsed) && parsed.every((v) => typeof v === 'string')) {
      return parsed as string[]
    }
  } catch {
    // ignore
  }
  return []
}

export default SettingEditor
